/**
 * Financial Section for Project Part Detail Page
 * Displays budget overview, offers, and invoiced amounts
 */

import React, { useMemo } from "react";
import {
  CaretUp,
  CaretDown,
  Warning,
} from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ProjectPart, DocumentType, DocumentApprovalStatus } from "shared/types/renovation";

interface PartFinancialSectionProps {
  part: ProjectPart;
}

// Format currency helper
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

export const PartFinancialSection: React.FC<PartFinancialSectionProps> = ({
  part,
}) => {
  // Calculate budget overview - using investmentAssignments (new system)
  const budgetOverview = useMemo(() => {
    // Use investmentAssignments as source of budget (new system)
    const assignedBudget =
      part.investmentAssignments?.reduce(
        (sum: number, assignment: any) =>
          sum + Number(assignment.assignedAmount),
        0
      ) || 0;

    const offers =
      part.documentLines
        ?.filter((da: any) => da.document.type === DocumentType.OFFER)
        .map((da: any) => {
          const documentTotal =
            da.document.lines?.reduce(
              (sum: number, line: any) =>
                sum + Math.abs(Number(line.totalInclVat || 0)),
              0
            ) || 0;
          return documentTotal;
        }) || [];

    const invoicedAmount =
      part.documentLines
        ?.filter((da: any) => da.document.type === DocumentType.INVOICE)
        .reduce((sum: number, da: any) => {
          const documentTotal =
            da.document.lines?.reduce(
              (lineSum: number, line: any) =>
                lineSum + Math.abs(Number(line.totalInclVat || 0)),
              0
            ) || 0;
          return sum + documentTotal;
        }, 0) || 0;

    return {
      assignedBudget,
      bestOffer: offers.length > 0 ? Math.min(...offers) : 0,
      worstOffer: offers.length > 0 ? Math.max(...offers) : 0,
      invoicedAmount,
      isOverBudget: invoicedAmount > assignedBudget,
      hasOffers: offers.length > 0,
    };
  }, [part]);

  // Get best offer with supplier info
  const bestOffer = useMemo(() => {
    const offers = part.documentLines
      ?.filter((da: any) => da.document.type === DocumentType.OFFER)
      .map((da: any) => ({
        amount:
          da.document.lines?.reduce(
            (sum: number, line: any) => sum + Number(line.totalInclVat || 0),
            0
          ) || 0,
        supplier: da.document.supplier,
      }))
      .filter((offer: any) => offer.amount > 0);

    if (!offers?.length) return null;
    return offers.reduce((best: any, current: any) =>
      current.amount < best.amount ? current : best
    );
  }, [part]);

  // Get approved offer
  const approvedOffer = useMemo(() => {
    return part.documentLines?.find(
      (da: any) =>
        da.document.type === DocumentType.OFFER &&
        da.document.status === DocumentApprovalStatus.APPROVED
    );
  }, [part]);

  // Get worst offer with supplier info
  const worstOffer = useMemo(() => {
    const offers = part.documentLines
      ?.filter((da: any) => da.document.type === DocumentType.OFFER)
      .map((da: any) => ({
        amount:
          da.document.lines?.reduce(
            (sum: number, line: any) => sum + Number(line.totalInclVat || 0),
            0
          ) || 0,
        supplier: da.document.supplier,
      }))
      .filter((offer: any) => offer.amount > 0);

    if (!offers?.length) return null;
    return offers.reduce((worst: any, current: any) =>
      current.amount > worst.amount ? current : worst
    );
  }, [part]);

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-4 border-b border-neutral-100 pb-2">
          <h4 className="text-sm font-medium text-neutral-700">
            Financieel overzicht
          </h4>
        </div>

        <div className="space-y-3">
          {/* Budget from Investment Assignments */}
          <div className="flex items-center justify-between border-b border-neutral-100 py-2">
            <span className="text-sm text-neutral-600">Budget:</span>
            <span className="text-sm font-medium">
              {(part.investmentAssignments?.length ?? 0) > 0 ? (
                formatCurrency(budgetOverview.assignedBudget)
              ) : (
                <span className="italic text-neutral-400">Niet toegewezen</span>
              )}
            </span>
          </div>

          {/* Approved/Chosen Offer */}
          <div className="flex items-center justify-between border-b border-neutral-100 py-2">
            <span className="text-sm text-neutral-600">Gekozen offerte:</span>
            <div className="flex items-center gap-2">
              {approvedOffer ? (
                <>
                  <Badge className="bg-moss-primary text-white">
                    {approvedOffer.document.supplier?.name}
                  </Badge>
                  <span className="text-sm font-medium">
                    {formatCurrency(
                      approvedOffer.document.lines?.reduce(
                        (sum: number, line: any) =>
                          sum + Number(line.totalInclVat || 0),
                        0
                      ) || 0
                    )}
                  </span>
                  <Tooltip>
                    <TooltipTrigger>
                      {(() => {
                        const amount =
                          approvedOffer.document.lines?.reduce(
                            (sum: number, line: any) =>
                              sum + Number(line.totalInclVat || 0),
                            0
                          ) || 0;
                        const isBetterThanBudget =
                          amount <= budgetOverview.assignedBudget;
                        return isBetterThanBudget ? (
                          <CaretDown
                            size={16}
                            className="text-green-600"
                            weight="bold"
                          />
                        ) : (
                          <CaretUp
                            size={16}
                            className="text-red-600"
                            weight="bold"
                          />
                        );
                      })()}
                    </TooltipTrigger>
                    <TooltipContent>
                      {(() => {
                        const amount =
                          approvedOffer.document.lines?.reduce(
                            (sum: number, line: any) =>
                              sum + Number(line.totalInclVat || 0),
                            0
                          ) || 0;
                        return amount <= budgetOverview.assignedBudget
                          ? "Binnen budget"
                          : "Boven budget";
                      })()}
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <span className="text-sm italic text-neutral-400">
                  Geen gekozen offerte
                </span>
              )}
            </div>
          </div>

          {/* Best Offer */}
          <div className="flex items-center justify-between border-b border-neutral-100 py-2">
            <span className="text-sm text-neutral-600">Beste offerte:</span>
            <div className="flex items-center gap-2">
              {bestOffer ? (
                <>
                  <Badge
                    variant="outline"
                    className="border-moss-primary text-moss-primary"
                  >
                    {bestOffer.supplier?.name}
                  </Badge>
                  <span className="text-sm font-medium">
                    {formatCurrency(bestOffer.amount)}
                  </span>
                  <Tooltip>
                    <TooltipTrigger>
                      {bestOffer.amount <= budgetOverview.assignedBudget ? (
                        <CaretDown
                          size={16}
                          className="text-green-600"
                          weight="bold"
                        />
                      ) : (
                        <CaretUp
                          size={16}
                          className="text-red-600"
                          weight="bold"
                        />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      {bestOffer.amount <= budgetOverview.assignedBudget
                        ? "Binnen budget"
                        : "Boven budget"}
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <span className="text-sm italic text-neutral-400">
                  Geen offertes
                </span>
              )}
            </div>
          </div>

          {/* Invoiced Amount */}
          <div className="flex items-center justify-between border-b border-neutral-100 py-2">
            <span className="text-sm text-neutral-600">Gefactureerd:</span>
            <div className="flex items-center gap-2">
              {budgetOverview.invoicedAmount > 0 ? (
                <>
                  <span
                    className={`text-sm font-medium ${
                      budgetOverview.isOverBudget
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {formatCurrency(budgetOverview.invoicedAmount)}
                    {budgetOverview.isOverBudget && (
                      <span className="ml-1">
                        (+
                        {formatCurrency(
                          budgetOverview.invoicedAmount -
                            budgetOverview.assignedBudget
                        )}
                        )
                      </span>
                    )}
                  </span>
                  {budgetOverview.isOverBudget && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Warning
                          size={16}
                          className="text-red-600"
                          weight="bold"
                        />
                      </TooltipTrigger>
                      <TooltipContent>Budget overschreden</TooltipContent>
                    </Tooltip>
                  )}
                </>
              ) : (
                <span className="text-sm italic text-neutral-400">
                  Geen facturen
                </span>
              )}
            </div>
          </div>

          {/* Worst Offer */}
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-neutral-600">Slechtste offerte:</span>
            <div className="flex items-center gap-2">
              {worstOffer ? (
                <>
                  <Badge
                    variant="outline"
                    className="border-brick-primary text-brick-primary"
                  >
                    {worstOffer.supplier?.name}
                  </Badge>
                  <span className="text-sm font-medium text-brick-primary">
                    {formatCurrency(worstOffer.amount)}
                  </span>
                  <Tooltip>
                    <TooltipTrigger>
                      {worstOffer.amount <= budgetOverview.assignedBudget ? (
                        <CaretDown
                          size={16}
                          className="text-green-600"
                          weight="bold"
                        />
                      ) : (
                        <CaretUp
                          size={16}
                          className="text-red-600"
                          weight="bold"
                        />
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      {worstOffer.amount <= budgetOverview.assignedBudget
                        ? "Binnen budget"
                        : "Boven budget"}
                    </TooltipContent>
                  </Tooltip>
                </>
              ) : (
                <span className="text-sm italic text-neutral-400">
                  Geen offertes
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PartFinancialSection;
